'use client';

import clsx from 'clsx';
import { cdn } from '@/lib/cdn';
import { Link } from '@/lib/i18n/navigation';

import type { CMSService, CMSServices as CMSServicesType } from '@/lib/cms/types';
import { CMSSection } from '../CmsSection';
import { Heading, type HeadingData } from '../Heading';

import styles from './Services.module.scss';

// ============================================
// Props Interface - Adapter Output ONLY
// ============================================

export interface ServicesProps extends Omit<
  CMSServicesType,
  '__typename' | 'sys' | 'internalName'
> {
  /** Layout variant */
  variant?: 'horizontal' | 'grid';
  /** Background color */
  backgroundColor?: string;
  /** Optional className */
  className?: string;
  /** CMS index for test IDs */
  cmsIndex?: number;
  /** Feature flag filter function */
  isServiceActive?: (serviceId: string | null | undefined) => boolean;
}

// ============================================
// Main Component
// ============================================

/**
 * Services component
 *
 * Displays a list of service icons/images with labels.
 * Accepts ONLY adapter output (CMSServices type).
 *
 * @example
 * <Services
 *   heading="Our Services"
 *   services={[
 *     { sys: { id: '1' }, title: 'Free Shipping', icon: { url: '...' }, cta: { url: '/shipping' } },
 *   ]}
 * />
 */
export function Services({
  heading,
  services,
  variant = 'horizontal',
  backgroundColor,
  className,
  cmsIndex,
  isServiceActive,
}: ServicesProps) {
  // Filter services if filter function is provided
  const visibleServices = (services || []).filter((service) => {
    if (!isServiceActive) return true;
    return isServiceActive(service.sys.id);
  });

  if (visibleServices.length === 0) {
    return null;
  }

  // Build heading data from string
  const headingData: HeadingData | null = heading ? { text: heading, headingType: 'h2' } : null;

  const renderService = (service: CMSService, index: number) => {
    const content = (
      <>
        <div className={styles.iconWrapper}>
          {service.icon?.url && (
            <img
              alt={service.title || 'Service icon'}
              src={cdn.transformImageUrl(service.icon.url, {
                width: 96,
                quality: 90,
                format: 'auto',
              })}
              className={styles.icon}
              data-testid="ServiceImg"
            />
          )}
        </div>
        <span className={styles.label} data-testid="ServiceLabel">
          {service.title}
        </span>
        {service.description && <span className={styles.description}>{service.description}</span>}
      </>
    );

    if (service.cta?.url) {
      return (
        <Link
          key={service.sys.id}
          href={service.cta.url}
          className={styles.service}
          data-testid={`Service-${index}`}
        >
          {content}
        </Link>
      );
    }

    return (
      <div key={service.sys.id} className={styles.service} data-testid={`Service-${index}`}>
        {content}
      </div>
    );
  };

  const testIdPrefix = cmsIndex !== undefined ? `services-${cmsIndex}` : 'ServicesSection';

  return (
    <CMSSection
      withMargin={false}
      className={clsx(styles.section, styles[variant], className)}
      style={backgroundColor ? { backgroundColor } : undefined}
      data-testid={testIdPrefix}
    >
      {headingData && (
        <div className={styles.headerWrapper}>
          <Heading
            Heading={headingData}
            sizeDesktop={40}
            sizeMobile={34}
            data-testid="ServicesHeading"
          />
        </div>
      )}
      <div className={styles.services}>
        {visibleServices.map((service, index) => renderService(service, index))}
      </div>
    </CMSSection>
  );
}

Services.displayName = 'Services';
